// ─────────────────────────────────────────────
// ALL SITE CONTENT LIVES HERE.
// Edit text in this one file — the whole site updates.
// ─────────────────────────────────────────────

export const profile = {
  name: "Ritu Mali",
  initials: "RM",
  roleLine: "Associate Product Manager — Sequencing, Roche Diagnostics",
  fullTimeRole: "Research Associate — Process Validation Engineering, cobas ePlex",
  positioning:
    "I help manage diagnostics products through their lifecycle — validation, launch, on-market care.",
  support:
    "Four years inside Roche Diagnostics across validation, quality control, and regulatory affairs. Currently working as an Associate PM in the Sequencing business.",
  email: "ritumali.biotech@gmail.com",
  linkedin: "https://www.linkedin.com/in/ritumali/",
  location: "San Diego, CA",
  resumePath: "/Ritu_Mali_Resume.pdf",
  chips: ["4+ yrs at Roche", "Science & Tech → Product", "NGS & IVD", "FDA · CE · IVDR"],
};

export const marqueeItems = [
  "Voice of Customer",
  "Target Product Profiles",
  "Launch Readiness",
  "Competitive Benchmarking",
  "On-market Product Care",
  "SPC & Process Data",
  "FDA · CE · IVDR",
  "Stakeholder Alignment",
];

export const about = [
  "I work where product meets process — the point where a diagnostic has to be manufacturable, compliant, and worth buying.",
  "Four years at Roche Diagnostics, across validation, quality control, and regulatory. I know how products actually ship, and why they stall.",
  "I think in trade-offs: market demand against cost, speed against traceability. I make the call with data, then align the room.",
  "The problems I like have real constraints — portfolio gaps, and products already on the market that need care.",
  "Right now I hold an Associate PM gig in Roche's Sequencing business, about 15 hours a week alongside my full-time role on the Science & Technology team. The plan is simple: make it full-time.",
];

export const personal =
  "I train — CrossFit and tennis — travel when I can, and I'm teaching myself sound engineering on the side.";

export const personalChips = ["CrossFit", "Tennis", "Travel", "Sound engineering"];

export const featuredProjects = [
  {
    index: "01",
    slug: "sequencing-portfolio",
    title: "Sequencing Sample Prep — Portfolio Strategy",
    context: "Associate PM gig · Roche Sequencing",
    description:
      "Competitive benchmarking of the sample prep portfolio, and Target Product Profiles built from Voice of Customer.",
    impact:
      "Portfolio gaps and differentiation opportunities identified — now feeding positioning. Sales-enablement one-pager shipped for commercial teams.",
    stack: ["Voice of Customer", "TPP", "Benchmarking", "Sales Enablement"],
  },
  {
    index: "02",
    slug: "eplex-demo-launch",
    title: "cobas ePlex Demo Consumable — Launch",
    context: "Process Validation, Science & Technology · Roche Diagnostics",
    description:
      "Translated IVD diagnostic functionality into a reagent-free demo consumable — built to grow sales by letting customers experience ePlex without running a live test.",
    impact:
      "Launch-ready across global operations, giving customers a way to experience ePlex without running a live test.",
    stack: ["Launch Readiness", "Cross-functional", "IVD"],
  },
];

// Full write-ups for the two featured projects (one page each).
export const caseStudies = [
  {
    slug: "sequencing-portfolio",
    index: "01",
    title: "Sequencing Sample Prep — Portfolio Strategy",
    context: "Associate Product Manager (gig) · Roche Sequencing Business Unit · 2025 — Present",
    stack: ["Voice of Customer", "TPP", "Benchmarking", "Sales Enablement"],
    problem:
      "The sequencing sample prep portfolio had no shared picture of where it stood against competitors. Commercial teams lacked a consolidated story, and customers landing on the website had no guided path from browsing the portfolio to the right product page.",
    process: [
      "Ran competitive benchmarking of the sample prep portfolio against key competitors, mapping where we were exposed and where we could win.",
      "Co-created Target Product Profiles with an International Product Manager, prioritizing core requirements from Voice of Customer input.",
      "Synthesized product content for the customer-facing website, so a customer moves from browsing to the right product page without getting lost.",
      "Distilled the portfolio's website and flyer content into a one-pager commercial teams could actually use.",
    ],
    results: [
      "Portfolio gaps and differentiation opportunities identified — now feeding positioning decisions.",
      "Portfolio-wide visibility delivered to a business leader across the full sequencing sample prep range.",
      "Sales-enablement one-pager shipped and in the hands of commercial teams.",
    ],
  },
  {
    slug: "eplex-demo-launch",
    index: "02",
    title: "cobas ePlex Demo Consumable — Launch",
    context: "Research Associate — Process Validation, Science & Technology · Roche Diagnostics",
    stack: ["Launch Readiness", "Cross-functional", "IVD"],
    problem:
      "Customers needed a way to experience ePlex diagnostic functionality without running a live diagnostic test. The answer was a reagent-free demo consumable — and it had to work across global operations from day one.",
    process: [
      "Translated IVD diagnostic functionality into a commercial-ready, reagent-free demo experience.",
      "Validated the demo workflow end to end so it mirrors the real product experience.",
      "Coordinated across global operations to confirm product readiness before launch.",
    ],
    results: [
      "Launch-ready across global operations.",
      "A demo customers can run that behaves like the real diagnostic — no reagents needed.",
    ],
  },
];

export const projects = [
  {
    index: "03",
    title: "Regulatory Intelligence Database",
    description:
      "Designed and built a SQL compliance database spanning 61+ countries and 4 IVD products.",
    impact: "Information retrieval 25% faster across EMEA and APAC.",
    stack: ["SQL", "Regulatory", "FDA · CE · IVDR"],
  },
  {
    index: "04",
    title: "Process Analytics at Scale",
    description:
      "Statistical process control across 100+ production batches, tied to cost targets and demand forecasting.",
    impact: "Waste down 12%. Process variability down 10%.",
    stack: ["SPC", "Data Analysis", "Cost Reduction"],
  },
  {
    index: "05",
    title: "ePlex Tower Kit",
    description:
      "Coordinated Operations, Commercial, and Supply Chain to land a compliant market strategy.",
    impact: "One aligned plan across three functions, delivered on schedule.",
    stack: ["Market Strategy", "Stakeholder Alignment"],
  },
  {
    index: "06",
    title: "NGS Workflow Optimization",
    description:
      "Optimized 3 NGS sample prep workflows, including Oxford Nanopore long-read library preparation.",
    impact: "Statistical analysis of genomic data across two clinical trials.",
    stack: ["NGS", "Library Prep", "R"],
  },
];

export const experience = [
  {
    role: "Associate Product Manager",
    org: "Roche Diagnostics — Sequencing Business Unit",
    dates: "2025 — Present",
    current: true,
    note: "Gig, ~15 hrs/week alongside full-time role",
    bullets: [
      "Co-creating Target Product Profiles with an International PM; prioritizing core requirements from Voice of Customer.",
      "Benchmarked the sequencing sample prep portfolio against key competitors. Found the gaps — now shaping positioning.",
      "Building portfolio content for the customer-facing website, guiding customers from browsing to product pages.",
    ],
  },
  {
    role: "Research Associate — Process Validation, Science & Technology",
    org: "Roche Diagnostics (GenMark)",
    dates: "Aug 2024 — Present",
    current: true,
    bullets: [
      "Ran SPC analysis across 100+ production batches. Waste down 12%, variability down 10%.",
      "Ran validation end to end for 2 new IVD product launches.",
      "Launched the cobas ePlex Demo Consumable — a reagent-free demo built to grow sales. Aligned Operations, Commercial, and Supply Chain on one market strategy; also partnered on the ePlex Tower Kit.",
      "Manage concurrent validation projects prioritized by market demand, aligning R&D, Tech Transfer, Software, Finance, and Manufacturing on deliverables.",
      "Cut product cost by streamlining process flow against standard manufacturing cost targets.",
    ],
  },
  {
    role: "Quality Control Specialist",
    org: "GenMark Diagnostics (Roche Group)",
    dates: "Jun 2023 — Aug 2024",
    bullets: [
      "Led pilot testing and validation for two new diagnostic assays and instruments; turned molecular performance data into go/no-go calls for commercial leadership.",
      "Investigated 25+ non-conformance events, coordinating root cause analysis and CAPA. Wrote the board-ready deviation reports.",
      "Shipped 7 structured process improvements through iterative testing. Defect rate down 10%.",
    ],
  },
  {
    role: "Regulatory Affairs Associate",
    org: "GenMark Diagnostics (Roche Group)",
    dates: "Oct 2022 — May 2023",
    bullets: [
      "Built a centralized SQL compliance database across 61+ countries and 4 IVD products. Retrieval 25% faster.",
      "Maintained registrations and compliance documentation across FDA, CE, and IVDR frameworks in EMEA and APAC.",
    ],
  },
  {
    role: "NGS Library Preparation Intern",
    org: "Volition America",
    dates: "May 2022 — Aug 2022",
    bullets: [
      "Optimized 3 NGS sample workflows, including Oxford Nanopore long-read prep.",
      "Analyzed genomic data across two clinical trials and designed the follow-up experiments.",
    ],
  },
  {
    role: "Molecular Biologist",
    org: "Lifenity Wellness International",
    dates: "Jul 2020 — Jan 2021",
    bullets: [
      "RT-PCR analysis and root cause work across 100+ plasma samples in 2 clinical trials.",
      "Partnered with Takeda on RT-PCR testing — cut processing time, raised output.",
    ],
  },
];

export const education = [
  {
    degree: "M.S. Biotechnology",
    school: "California State University, San Marcos",
    year: "2023",
    note: "Al Kern Award for Best Master's Thesis",
  },
  {
    degree: "B.E. Biotechnology",
    school: "Shivaji University, India",
    year: "2020",
  },
];

export const skillGroups = [
  {
    label: "Product",
    items: [
      "Voice of Customer",
      "Target Product Profiles",
      "Competitive Benchmarking",
      "Launch Readiness",
      "Sales Enablement",
      "Prioritization",
      "Lifecycle Management",
      "Stakeholder Alignment",
    ],
  },
  {
    label: "Data",
    items: ["SQL", "R", "SPC", "Genomic Data Analysis", "Google Data Analytics (Cert.)"],
  },
  {
    label: "Tools",
    items: ["FMEA", "CAPA", "Root Cause Analysis", "IQ/OQ/PQ", "Deviation Reports", "Statistical Methods"],
  },
  {
    label: "Tech",
    items: ["NGS Library Prep", "Oxford Nanopore", "RT-PCR", "21 CFR Part 820", "ISO 13485", "FDA · CE · IVDR"],
  },
];

export const languages = "English (fluent) · German (A1, learning)";

export const contact = {
  heading: "Let's talk.",
  line: "I'm moving into product management full-time. If you're building in diagnostics, sequencing, or anything with real constraints — I'd like to hear about it.",
  closing: '"Pressure is a privilege." — Billie Jean King',
};

export const footer = {
  line: "© 2026 Ritu Mali · San Diego, CA",
  note: "Written and built by hand.",
};
